import * as React from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import { createHashRouter, RouterProvider, Navigate } from 'react-router';
import DashboardLayout from './components/DashboardLayout';
import EmployeeList from './components/EmployeeList';
import EmployeeShow from './components/EmployeeShow';
import EmployeeCreate from './components/EmployeeCreate';
import EmployeeEdit from './components/EmployeeEdit';
import { NotificationProvider } from './components/CustomNotification';
import { FirebaseNotificationListener } from './components/FirebaseNotificationListener';
import DialogsProvider from './hooks/useDialogs/DialogsProvider';
import AppTheme from '../shared-theme/AppTheme';
import { LanguageProvider } from '../contexts/LanguageContext';
import { AuthProvider, useAuth } from '../contexts/AuthContext';
import LoginPage from '../components/LoginPage';
import ProtectedRoute from '../components/ProtectedRoute';
import RoleBasedRoute from '../components/RoleBasedRoute';
import {
  dataGridCustomizations,
  datePickersCustomizations,
  sidebarCustomizations,
  formInputCustomizations,
} from './theme/customizations';
import OrderEdit from "./components/OrderEdit";
import OrderCreate from "./components/OrderCreate";
import OrderShow from "./components/OrderShow";
import OrderList from "./components/OrderList";
import OfferEdit from "./components/OfferEdit";
import OfferShow from "./components/OfferShow";
import OfferList from "./components/OfferList";
import OfferCreate from "./components/OfferCreate";
import Payments from "./components/MyPayments";
import OrdersStatistics from "./components/OrdersStatistics";
import LeaveRequestList from "./components/LeaveRequestList";
import LeaveRequestCreate from "./components/LeaveRequestCreate";
import CountryList from "./components/CountryList";
import BroadcastMessages from "./components/BroadcastMessages";
import AttendanceList from "./components/AttendanceList";

// Protected Dashboard Layout Wrapper
function ProtectedDashboardLayout() {
  return (
    <ProtectedRoute>
      <DashboardLayout />
    </ProtectedRoute>
  );
}

// Home redirect based on user role
function HomeRedirect() {
  const { user } = useAuth();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // If user is GM, redirect to employees list
  if (user.role === 'GM' || user.role === 'BM') {
    return <Navigate to="/employees" replace />;
  }

  // Otherwise, redirect to user's own profile
  return <Navigate to={`/employees/${user.id}`} replace />;
}

// Wrapper components for role-based route protection
function EmployeeListProtected() {
  return (
    <RoleBasedRoute allowedRoles={['GM', 'BM']}>
      <EmployeeList />
    </RoleBasedRoute>
  );
}

function EmployeeCreateProtected() {
  return (
    <RoleBasedRoute allowedRoles={['GM', 'BM']}>
      <EmployeeCreate />
    </RoleBasedRoute>
  );
}

function EmployeeEditProtected() {
  return (
    <RoleBasedRoute allowedRoles={['GM', 'BM']}>
      <EmployeeEdit />
    </RoleBasedRoute>
  );
}

function OfferCreateProtected() {
  return (
    <RoleBasedRoute allowedRoles={['GM', 'BM']}>
      <OfferCreate />
    </RoleBasedRoute>
  );
}

function OfferEditProtected() {
  return (
    <RoleBasedRoute allowedRoles={['GM', 'BM']}>
      <OfferEdit />
    </RoleBasedRoute>
  );
}

function OrdersStatisticsProtected() {
  return (
    <RoleBasedRoute allowedRoles={['GM']}>
      <OrdersStatistics />
    </RoleBasedRoute>
  );
}

function CountryListProtected() {
  return (
    <RoleBasedRoute allowedRoles={['GM', 'BM']}>
      <CountryList />
    </RoleBasedRoute>
  );
}

function AttendanceListProtected() {
  return (
    <RoleBasedRoute allowedRoles={['GM']}>
      <AttendanceList />
    </RoleBasedRoute>
  );
}

const router = createHashRouter([
  {
    path: '/login',
    Component: LoginPage,
  },
  {
    Component: ProtectedDashboardLayout,
    children: [
      {
        path: '/',
        Component: HomeRedirect,
      },
      {
        path: '/employees',
        Component: EmployeeListProtected,
      },
      {
        path: '/employees/:employeeId',
        Component: EmployeeShow,
      },
      {
        path: '/employees/new',
        Component: EmployeeCreateProtected,
      },
      {
        path: '/employees/:employeeId/edit',
        Component: EmployeeEditProtected,
      },
      {
        path: '/orders',
        Component: OrderList,
      },
      {
        path: '/orders/:orderId',
        Component: OrderShow,
      },
      {
        path: '/orders/new',
        Component: OrderCreate,
      },
      {
        path: '/orders/:orderId/edit',
        Component: OrderEdit,
      },
      {
        path: '/offers',
        Component: OfferList,
      },
      {
        path: '/offers/:offerId',
        Component: OfferShow,
      },
      {
        path: '/offers/new',
        Component: OfferCreateProtected,
      },
      {
        path: '/offers/:offerId/edit',
        Component: OfferEditProtected,
      },
      {
        path: '/payments',
        Component: Payments,
      },
      {
        path: '/statistics',
        Component: OrdersStatisticsProtected,
      },
      {
        path: '/leave-requests',
        Component: LeaveRequestList,
      },
      {
        path: '/leave-requests/new',
        Component: LeaveRequestCreate,
      },
      {
        path: '/countries',
        Component: CountryListProtected,
      },
      {
        path: '/broadcasts',
        Component: BroadcastMessages,
      },
      {
        path: '/attendance',
        Component: AttendanceListProtected,
      },
      // Fallback route
      {
        path: '*',
        Component: HomeRedirect,
      },
    ],
  },
]);

const themeComponents = {
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...sidebarCustomizations,
  ...formInputCustomizations,
};

export default function CrudDashboard(props: { disableCustomTheme?: boolean }) {
  return (
    <LanguageProvider>
      <AuthProvider>
        <AppTheme {...props} themeComponents={themeComponents}>
          <CssBaseline enableColorScheme />
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <NotificationProvider>
              <FirebaseNotificationListener />
              <DialogsProvider>
                <RouterProvider router={router} />
              </DialogsProvider>
            </NotificationProvider>
          </Box>
        </AppTheme>
      </AuthProvider>
    </LanguageProvider>
  );
}
